// ⚡️ CHAT STATE MANAGEMENT: State management hook for chat functionality
// Extracted from ChatPanel.js for better organization and reusability

import { useReducer } from 'react';

// ⚡️ PERFORMANCE OPTIMIZATION: State reducer for better state management
export const initialState = {
  input: '',
  showEmojiPicker: false,
  sending: false,
  loadingMore: false,
  showTick: false,
  firestoreLoading: false,
  uploadingFile: null,
  uploading: false,
  uploadProgress: 0,
  uploadedFileUrl: null,
  openedImage: null,
  readMessages: {},
  messageStatus: {},
  showProductModal: false,
  isDragOver: false,
  pendingMessages: [],
  lastVisible: null,
  hasMore: true,
  firestoreMessages: [],
  firestoreConvId: null,
  localFirestoreConvId: null,
  collapsedBoxes: {
    orderDetails: false,
    escrowWorks: false,
    transactionStatus: false,
  }
};

export const chatReducer = (state, action) => {
  switch (action.type) {
    case 'SET_INPUT':
      return { ...state, input: action.payload };
    case 'TOGGLE_EMOJI_PICKER':
      return { ...state, showEmojiPicker: !state.showEmojiPicker };
    case 'SET_SENDING':
      return { ...state, sending: action.payload };
    case 'SET_LOADING_MORE':
      return { ...state, loadingMore: action.payload };
    case 'SET_SHOW_TICK':
      return { ...state, showTick: action.payload };
    case 'SET_FIRESTORE_LOADING':
      return { ...state, firestoreLoading: action.payload };
    case 'SET_UPLOADING_FILE':
      return { ...state, uploadingFile: action.payload };
    case 'SET_UPLOADING':
      return { ...state, uploading: action.payload };
    case 'SET_UPLOAD_PROGRESS':
      return { ...state, uploadProgress: action.payload };
    case 'SET_UPLOADED_FILE_URL':
      return { ...state, uploadedFileUrl: action.payload };
    case 'SET_OPENED_IMAGE':
      return { ...state, openedImage: action.payload };
    case 'UPDATE_READ_MESSAGES':
      return { 
        ...state, 
        readMessages: { ...state.readMessages, ...action.payload }
      };
    case 'UPDATE_MESSAGE_STATUS':
      return { 
        ...state, 
        messageStatus: { ...state.messageStatus, ...action.payload }
      };
    case 'TOGGLE_BOX_COLLAPSE':
      return { 
        ...state, 
        collapsedBoxes: { 
          ...state.collapsedBoxes, 
          [action.payload]: !state.collapsedBoxes[action.payload] 
        } 
      };
    case 'SET_SHOW_PRODUCT_MODAL':
      return { ...state, showProductModal: action.payload };
    case 'SET_DRAG_OVER':
      return { ...state, isDragOver: action.payload };
    case 'ADD_PENDING_MESSAGE':
      return { 
        ...state, 
        pendingMessages: [...state.pendingMessages, action.payload]
      };
    case 'UPDATE_PENDING_MESSAGES':
      return { ...state, pendingMessages: action.payload };
    case 'REPLACE_PENDING_MESSAGE':
      return {
        ...state,
        pendingMessages: state.pendingMessages.map(msg => 
          msg.id === action.payload.tempId 
            ? action.payload.realMessage 
            : msg
        )
      };
    case 'UPDATE_PENDING_MESSAGE':
      return {
        ...state,
        pendingMessages: state.pendingMessages.map(msg => 
          msg.id === action.payload.id 
            ? { ...msg, status: action.payload.status, error: action.payload.error }
            : msg
        )
      };
    case 'CLEANUP_PENDING_MESSAGES':
      return {
        ...state,
        pendingMessages: action.payload
      };
    case 'SET_LAST_VISIBLE':
      return { ...state, lastVisible: action.payload };
    case 'SET_HAS_MORE':
      return { ...state, hasMore: action.payload };
    case 'SET_FIRESTORE_MESSAGES':
      return { ...state, firestoreMessages: action.payload };
    case 'SET_LOCAL_FIRESTORE_CONV_ID':
      return { ...state, localFirestoreConvId: action.payload };
    case 'SET_FIRESTORE_CONV_ID':
      return { ...state, firestoreConvId: action.payload };
    case 'RESET_UPLOAD_STATE':
      return { 
        ...state, 
        uploadingFile: null,
        uploading: false,
        uploadProgress: 0,
        uploadedFileUrl: null
      };
    default:
      return state;
  }
};

// Custom hook for chat state management
export const useChatState = () => {
  const [state, dispatch] = useReducer(chatReducer, initialState);
  
  // Helper functions for common state updates
  const setInput = (input) => dispatch({ type: 'SET_INPUT', payload: input });
  const toggleEmojiPicker = () => dispatch({ type: 'TOGGLE_EMOJI_PICKER' });
  const setSending = (sending) => dispatch({ type: 'SET_SENDING', payload: sending });
  const setLoadingMore = (loading) => dispatch({ type: 'SET_LOADING_MORE', payload: loading });
  const setShowTick = (show) => dispatch({ type: 'SET_SHOW_TICK', payload: show });
  const setFirestoreLoading = (loading) => dispatch({ type: 'SET_FIRESTORE_LOADING', payload: loading });
  const setUploadingFile = (file) => dispatch({ type: 'SET_UPLOADING_FILE', payload: file });
  const setUploading = (uploading) => dispatch({ type: 'SET_UPLOADING', payload: uploading });
  const setUploadProgress = (progress) => dispatch({ type: 'SET_UPLOAD_PROGRESS', payload: progress });
  const setUploadedFileUrl = (url) => dispatch({ type: 'SET_UPLOADED_FILE_URL', payload: url });
  const setOpenedImage = (image) => dispatch({ type: 'SET_OPENED_IMAGE', payload: image });
  const updateReadMessages = (messages) => dispatch({ type: 'UPDATE_READ_MESSAGES', payload: messages });
  const updateMessageStatus = (status) => dispatch({ type: 'UPDATE_MESSAGE_STATUS', payload: status });
  const toggleBoxCollapse = (boxName) => dispatch({ type: 'TOGGLE_BOX_COLLAPSE', payload: boxName });
  const setShowProductModal = (show) => dispatch({ type: 'SET_SHOW_PRODUCT_MODAL', payload: show });
  const setDragOver = (isDragOver) => dispatch({ type: 'SET_DRAG_OVER', payload: isDragOver });
  const addPendingMessage = (message) => dispatch({ type: 'ADD_PENDING_MESSAGE', payload: message });
  const updatePendingMessages = (messages) => dispatch({ type: 'UPDATE_PENDING_MESSAGES', payload: messages });
  const replacePendingMessage = (tempId, realMessage) => dispatch({ 
    type: 'REPLACE_PENDING_MESSAGE', 
    payload: { tempId, realMessage } 
  });
  const updatePendingMessage = (id, status, error) => dispatch({ 
    type: 'UPDATE_PENDING_MESSAGE', 
    payload: { id, status, error } 
  });
  const cleanupPendingMessages = (messages) => dispatch({ type: 'CLEANUP_PENDING_MESSAGES', payload: messages });
  const setLastVisible = (lastVisible) => dispatch({ type: 'SET_LAST_VISIBLE', payload: lastVisible });
  const setHasMore = (hasMore) => dispatch({ type: 'SET_HAS_MORE', payload: hasMore });
  const setFirestoreMessages = (messages) => dispatch({ type: 'SET_FIRESTORE_MESSAGES', payload: messages });
  const setLocalFirestoreConvId = (convId) => dispatch({ type: 'SET_LOCAL_FIRESTORE_CONV_ID', payload: convId });
  const setFirestoreConvId = (convId) => dispatch({ type: 'SET_FIRESTORE_CONV_ID', payload: convId });
  const resetUploadState = () => dispatch({ type: 'RESET_UPLOAD_STATE' });
  
  return {
    state,
    dispatch,
    // Helper functions
    setInput,
    toggleEmojiPicker,
    setSending,
    setLoadingMore,
    setShowTick,
    setFirestoreLoading,
    setUploadingFile,
    setUploading,
    setUploadProgress,
    setUploadedFileUrl,
    setOpenedImage,
    updateReadMessages,
    updateMessageStatus,
    toggleBoxCollapse,
    setShowProductModal,
    setDragOver,
    addPendingMessage,
    updatePendingMessages,
    replacePendingMessage,
    updatePendingMessage,
    cleanupPendingMessages,
    setLastVisible,
    setHasMore,
    setFirestoreMessages,
    setLocalFirestoreConvId,
    setFirestoreConvId,
    resetUploadState
  };
};
